import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import apiClient from "../api/client";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import "./ActivityIntakeModal.css";
import { getDefaultRunName } from "../utils/activityNames";

const defaultForm = () => ({
  name: "",
  startDate: new Date().toISOString().slice(0, 16),
  distance: "",
  distanceUnit: "kilometers", // kilometers, meters, miles, yards
  durationHours: "0",
  durationMinutes: "",
  durationSeconds: "0",
  elevationGain: "",
  elevationUnit: "meters", // meters, feet
  avgHr: "",
  maxHr: "",
  notes: "",
  perceivedEffort: ""
});

const ManualActivityForm = ({ isOpen, onClose, email = "", onSuccess = undefined, onError = undefined }) => {
  const { unit } = useDistanceUnit();
  const [formState, setFormState] = useState(() => defaultForm());
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const form = defaultForm();
      // Set default distance unit based on user preference
      form.distanceUnit = unit === 'miles' ? 'miles' : 'kilometers';
      form.name = getDefaultRunName(form.startDate);
      setFormState(form);
      setError(null);
    }
  }, [isOpen, unit]);

  const handleFieldChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const closeModal = () => {
    setError(null);
    onClose();
  };

  const buildPayload = () => {
    // Convert distance to meters based on selected unit
    const distance = parseFloat(formState.distance);
    if (Number.isNaN(distance) || distance <= 0) {
      throw new Error("Please enter a valid distance.");
    }

    let distanceMeters;
    switch (formState.distanceUnit) {
      case "kilometers":
        distanceMeters = distance * 1000;
        break;
      case "meters":
        distanceMeters = distance;
        break;
      case "miles":
        distanceMeters = distance * 1609.34;
        break;
      case "yards":
        distanceMeters = distance * 0.9144;
        break;
      default:
        distanceMeters = distance * 1000;
    }

    // Convert duration to seconds
    const hours = parseInt(formState.durationHours) || 0;
    const minutes = parseInt(formState.durationMinutes) || 0;
    const seconds = parseInt(formState.durationSeconds) || 0;
    const durationSeconds = hours * 3600 + minutes * 60 + seconds;

    if (durationSeconds <= 0) {
      throw new Error("Please enter a valid duration.");
    }

    if (!formState.startDate) {
      throw new Error("Please select a start date and time.");
    }

    // Convert elevation to meters based on selected unit
    let elevationMeters = null;
    if (formState.elevationGain !== "") {
      const elevation = parseFloat(formState.elevationGain);
      if (!Number.isNaN(elevation)) {
        elevationMeters = formState.elevationUnit === "feet" ? elevation * 0.3048 : elevation;
      }
    }

    const resolvedName =
      (formState.name && formState.name.trim()) ||
      getDefaultRunName(formState.startDate);

    return {
      source_type: "manual",
      name: resolvedName,
      distance: Math.round(distanceMeters),
      moving_time: durationSeconds,
      elapsed_time: durationSeconds,
      start_date_local: formState.startDate,
      total_elevation_gain: elevationMeters,
      average_heartrate:
        formState.avgHr !== "" ? Number(formState.avgHr) : null,
      max_heartrate:
        formState.maxHr !== "" ? Number(formState.maxHr) : null,
      notes: formState.notes,
      perceived_effort:
        formState.perceivedEffort !== ""
          ? Number(formState.perceivedEffort)
          : null,
      is_manual: true
    };
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      setError(null);
      const payload = buildPayload();
      // Remove email from payload - it's passed via JWT
      delete payload.email;
      console.log('Manual activity payload:', JSON.stringify(payload, null, 2));
      await apiClient.post('/activities', payload);
      if (onSuccess) {
        onSuccess("Manual run saved.");
      }
      closeModal();
    } catch (saveError) {
      console.error("Failed to save manual activity", saveError);
      console.error("Error response:", saveError.response?.data);

      // Extract all validation errors and format them nicely
      let message;
      if (saveError.response?.data?.messages && Array.isArray(saveError.response.data.messages)) {
        // Join all validation error messages
        const errorMessages = saveError.response.data.messages.map(msg => {
          // Convert error codes to user-friendly messages
          const errorMap = {
            'INVALID_START_DATE': 'Invalid start date/time',
            'INVALID_DISTANCE': 'Invalid distance (must be greater than 0)',
            'INVALID_MOVING_TIME': 'Invalid duration (must be greater than 0)',
            'INVALID_ELAPSED_TIME': 'Invalid elapsed time',
            'UNSUPPORTED_SOURCE_TYPE': 'Invalid activity source type',
            'DUPLICATE_ACTIVITY': 'This activity already exists'
          };
          return errorMap[msg] || msg;
        });
        message = errorMessages.join('. ');
      } else {
        message =
          saveError.message ||
          saveError.response?.data?.messages?.[0] ||
          saveError.response?.data?.error ||
          "Unable to save manual run.";
      }

      setError(message);
      if (onError) {
        onError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="activity-modal-backdrop">
      <div className="activity-modal">
        <div className="activity-modal-header">
          <h2 className="activity-modal-title">Add Manual Run</h2>
          <button
            type="button"
            className="activity-modal-close"
            onClick={closeModal}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="activity-modal-body">
          <p className="activity-modal-subtitle">
            Quickly log a run without a file. Choose your preferred units for distance and elevation.
            Distance, duration, and start time are required.
          </p>

          {error && <div className="activity-modal-error">{error}</div>}

          <div className="activity-modal-field">
            <label htmlFor="manual-name">Activity Name</label>
            <input
              id="manual-name"
              type="text"
              value={formState.name}
              onChange={(event) =>
                handleFieldChange("name", event.target.value)
              }
              placeholder="Morning Run"
            />
          </div>

          <div className="activity-modal-field">
            <label htmlFor="manual-date">Start Time</label>
            <input
              id="manual-date"
              type="datetime-local"
              value={formState.startDate}
              onChange={(event) =>
                handleFieldChange("startDate", event.target.value)
              }
            />
          </div>

          <div className="activity-modal-grid">
            <div className="activity-modal-field">
              <label htmlFor="manual-distance">Distance</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  id="manual-distance"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formState.distance}
                  onChange={(event) =>
                    handleFieldChange("distance", event.target.value)
                  }
                  style={{ flex: 1 }}
                />
                <select
                  value={formState.distanceUnit}
                  onChange={(event) =>
                    handleFieldChange("distanceUnit", event.target.value)
                  }
                  style={{ minWidth: '120px' }}
                >
                  <option value="kilometers">kilometers</option>
                  <option value="meters">meters</option>
                  <option value="miles">miles</option>
                  <option value="yards">yards</option>
                </select>
              </div>
            </div>

            <div className="activity-modal-field">
              <label htmlFor="manual-elevation">Elevation Gain</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  id="manual-elevation"
                  type="number"
                  step="1"
                  value={formState.elevationGain}
                  onChange={(event) =>
                    handleFieldChange("elevationGain", event.target.value)
                  }
                  style={{ flex: 1 }}
                />
                <select
                  value={formState.elevationUnit}
                  onChange={(event) =>
                    handleFieldChange("elevationUnit", event.target.value)
                  }
                  style={{ minWidth: '100px' }}
                >
                  <option value="meters">meters</option>
                  <option value="feet">feet</option>
                </select>
              </div>
            </div>
          </div>

          <div className="activity-modal-field">
            <label>Duration</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                min="0"
                max="23"
                value={formState.durationHours}
                onChange={(event) =>
                  handleFieldChange("durationHours", event.target.value)
                }
                placeholder="0"
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '20px' }}>hr</span>
              <input
                type="number"
                min="0"
                max="59"
                value={formState.durationMinutes}
                onChange={(event) =>
                  handleFieldChange("durationMinutes", event.target.value)
                }
                placeholder="0"
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '30px' }}>min</span>
              <input
                type="number"
                min="0"
                max="59"
                value={formState.durationSeconds}
                onChange={(event) =>
                  handleFieldChange("durationSeconds", event.target.value)
                }
                placeholder="0"
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '30px' }}>sec</span>
            </div>
          </div>

          <div className="activity-modal-grid">
            <div className="activity-modal-field">
              <label htmlFor="manual-avg-hr">Avg HR</label>
              <input
                id="manual-avg-hr"
                type="number"
                min="0"
                step="1"
                value={formState.avgHr}
                onChange={(event) =>
                  handleFieldChange("avgHr", event.target.value)
                }
              />
            </div>
            <div className="activity-modal-field">
              <label htmlFor="manual-max-hr">Max HR</label>
              <input
                id="manual-max-hr"
                type="number"
                min="0"
                step="1"
                value={formState.maxHr}
                onChange={(event) =>
                  handleFieldChange("maxHr", event.target.value)
                }
              />
            </div>
            <div className="activity-modal-field">
              <label htmlFor="manual-effort">Perceived Effort (1-10)</label>
              <input
                id="manual-effort"
                type="number"
                min="1"
                max="10"
                step="1"
                value={formState.perceivedEffort}
                onChange={(event) =>
                  handleFieldChange("perceivedEffort", event.target.value)
                }
              />
            </div>
          </div>

          <div className="activity-modal-field">
            <label htmlFor="manual-notes">Notes</label>
            <textarea
              id="manual-notes"
              value={formState.notes}
              onChange={(event) =>
                handleFieldChange("notes", event.target.value)
              }
              placeholder="Optional comments about this run"
            />
          </div>
        </div>

        <div className="activity-modal-footer">
          <button
            type="button"
            className="activity-modal-button secondary"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="activity-modal-button primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : "Save Run"}
          </button>
        </div>
      </div>
    </div>
  );
};

ManualActivityForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
};


export default ManualActivityForm;
